import React, { useState, useMemo, useEffect } from 'react';

import { Form, Row, Col, Typography, Button, Space, Steps } from 'antd';

import {
  CheckCircleFilled,
  LoadingOutlined,
  CloseCircleFilled,
  CreditCardFilled,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  UndoOutlined,
} from '@ant-design/icons';

import {
  StyledModal,
  FormSaveButton,
  FormButton,
  WithLoading,
  dialogFunction,
} from 'common/components';

import { useQueryClient } from '@tanstack/react-query';

import { productFullViewKeys, useVisibleModal } from 'hooks';

import {
  useMutateMakeProductActive,
  useValidateActiveProduct,
} from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';

import { mappingGridProperties as groupPropertiesByModuleName } from 'pages/home/ribbon/components/sections/customize-grid/utils';

import { getPropertiesParams } from 'utils/minimumRequirementUtils';
import {
  IXOneGrid,
  IXOneHeader,
  IXOneMessage,
  IXOneTotalFee,
  RIVIRBillingReview,
  useIXOneColumns,
} from 'pages/home/ribbon/components/sections/product/components/ixone-credit-card';

import { AdditionalRequiredActiveProduct } from './AdditionalRequiredActiveProduct';
import { RequiredPropertyInput } from './RequiredPropertyInput';

import {
  RequiredModuleName,
  RequiredPropertiesForm,
  RequiredPropertiesSection,
  RequiredPropertyItem,
  RequiredPropertyName,
  RequiredPropertyStatus,
} from './RequiredPropertyElement';

import {
  COD_PAYMENT_TYPE_VIEW,
  CODChargeValidating,
} from 'pages/home/ribbon/components/sections/product/components/cod-payment';

import { ContentConfirmRequest } from 'pages/home/ribbon/components/sections/product/components/ixone-section/IXOneCapture';

import {
  RequiredActiveProductContext,
  useCalculateFeeActiveProduct,
  useGetMinimumRequirementDetail,
  useRequiredActiveProductContext,
  useUpdateUnmatchedRequirement,
  useValidateMinimumRequirement,
  validateMinimumRequirementKeys,
} from './hooks';

import { filterInvalidValuesFormValues } from './utils';

import { useAllMinimumRequirementProductFields } from 'hooks/entityHooks';
import { useCheckPaymentTerm } from 'pages/home/ribbon/components/sections/product/components/cod-payment/hooks';

import {
  STEP_CHARGE,
  STEP_REQUIREMENT,
  additionalRequirementFields,
} from './constants';
import { keys } from 'pages/company-profile/components/tabs/billing/queries';

import './RequiredActiveProduct.less';

const { Step } = Steps;

export const RequiredActiveProductRoot = ({ children }) => {
  const [requiredForm] = Form.useForm();

  const [currentStep, setCurrentStep] = useState(STEP_REQUIREMENT);

  return (
    <RequiredActiveProductContext.Provider
      value={{ requiredForm, currentStep, setCurrentStep }}
    >
      {children}
    </RequiredActiveProductContext.Provider>
  );
};

export const RequiredActiveProductModal = ({
  entityId,
  entityType,
  onMakeActiveSuccessCallback = () => {},
  children,
  onCancel,
  ...otherProps
}) => {
  const queryClient = useQueryClient();

  const { requiredForm, currentStep, setCurrentStep } =
    useRequiredActiveProductContext();

  const unmatchedRequirementMutation = useUpdateUnmatchedRequirement();

  const { entityRequirement, isEmptyEntityRequirement } =
    useGetMinimumRequirementDetail({
      entityId,
      entityName: entityType,
    });

  const totalFee = useCalculateFeeActiveProduct({ productIds: [entityId] });

  const { requiredErrorFields, isMatched } = useValidateMinimumRequirement({
    requirementId: entityRequirement?.id,
    productId: entityId,
  });

  const { passedProducts } = useValidateActiveProduct({
    productIds: [entityId],
  });

  const [
    visibleReviewBilling,
    { onModal: onModalReviewBilling, offModal: offModalReviewBilling },
  ] = useVisibleModal();

  const { isCODTerm, isInvoicedTerm } = useCheckPaymentTerm();

  useEffect(() => {
    if (isMatched) setCurrentStep(STEP_CHARGE);
  }, [isMatched, setCurrentStep]);

  const activeProductChargeMutation = useMutateMakeProductActive({
    meta: {
      invalidates: [
        productFullViewKeys.itemData(entityId),
        keys.allMemberBillingStatement(),
      ],
    },
  });

  const isRequirementStep = currentStep === 0;

  const confirmRequest = async () => {
    const params = {
      productIds: [entityId],
    };
    await activeProductChargeMutation.mutateAsync(params, {
      onSuccess: () => {
        onMakeActiveSuccessCallback();
        onCancel();
      },
    });
  };

  const handleMakeProductActive = () => {
    dialogFunction({
      type: 'warn',
      content: <ContentConfirmRequest onClick={onModalReviewBilling} />,
      okText: 'Agree',
      onOk: confirmRequest,
    });
  };

  const handleRevalidateMinimumRequirement = () => {
    queryClient.invalidateQueries({
      queryKey: validateMinimumRequirementKeys({
        requirementId: entityRequirement?.id,
        productId: entityId,
      }),
    });
  };

  const handleUpdateUnmatchedRequirement = () => {
    const formValues = filterInvalidValuesFormValues(
      requiredForm.getFieldsValue()
    );

    const requestParams = {
      entityId,
      entityName: 'PIM',
      properties: getPropertiesParams(formValues),
    };

    unmatchedRequirementMutation.mutate(requestParams, {
      onSuccess: () => {
        handleRevalidateMinimumRequirement();
        setCurrentStep(STEP_CHARGE);
      },
    });
  };

  const isDisabledNextButton = requiredErrorFields?.length > 0;
  const isDisabledBackButton = requiredErrorFields?.length === 0;

  const modalProps = {
    title: 'Update Required Values',
    width: '85%',
    closable: true,
    maskClosable: false,
    bodyStyle: {
      height: '1200px',
    },
    onCancel,
    wrapClassName: 'required-section__modal',
    ...otherProps,
  };

  return (
    <CODChargeValidating
      fee={totalFee}
      typeView={COD_PAYMENT_TYPE_VIEW.activeProduct}
      confirmRequest={confirmRequest}
      chargeItems={passedProducts}
    >
      {({ handleCODCharge }) => {
        return (
          <StyledModal
            {...modalProps}
            footer={
              <>
                {isEmptyEntityRequirement ? (
                  <Space>
                    <FormButton
                      text='Back'
                      type='secondary'
                      icon={null}
                      disabled={
                        isEmptyEntityRequirement || isDisabledBackButton
                      }
                      onClick={() => setCurrentStep(STEP_REQUIREMENT)}
                    />
                    <FormSaveButton
                      text='Confirm Request'
                      onClick={() => {
                        if (isCODTerm) {
                          handleCODCharge();
                        } else if (isInvoicedTerm) {
                          confirmRequest();
                        } else {
                          handleMakeProductActive();
                        }
                      }}
                      disabled={!passedProducts?.length}
                      loading={activeProductChargeMutation.isLoading}
                    />
                  </Space>
                ) : (
                  <>
                    {isRequirementStep ? (
                      <Space>
                        <FormButton
                          text='Next'
                          type='secondary'
                          icon={<ArrowRightOutlined />}
                          disabled={isDisabledNextButton}
                          onClick={() => setCurrentStep(STEP_CHARGE)}
                        />
                        <FormButton
                          text='Revalidate'
                          icon={<UndoOutlined />}
                          onClick={handleRevalidateMinimumRequirement}
                        />
                        <FormSaveButton
                          text='Submit'
                          onClick={handleUpdateUnmatchedRequirement}
                          loading={unmatchedRequirementMutation.isLoading}
                          disabled={!isDisabledNextButton}
                        />
                      </Space>
                    ) : (
                      <Space>
                        <FormButton
                          text='Back'
                          type='secondary'
                          icon={<ArrowLeftOutlined />}
                          disabled={
                            isEmptyEntityRequirement || isDisabledBackButton
                          }
                          onClick={() => setCurrentStep(STEP_REQUIREMENT)}
                        />
                        <FormSaveButton
                          text='Confirm Request'
                          onClick={() => {
                            if (isCODTerm) {
                              handleCODCharge();
                            } else if (isInvoicedTerm) {
                              confirmRequest();
                            } else {
                              handleMakeProductActive();
                            }
                          }}
                          disabled={!passedProducts?.length}
                          loading={activeProductChargeMutation.isLoading}
                        />
                      </Space>
                    )}
                  </>
                )}
              </>
            }
          >
            <WithLoading
              loading={unmatchedRequirementMutation.isLoading}
              tip='Validating...'
            >
              {children}
            </WithLoading>
            {visibleReviewBilling && (
              <RIVIRBillingReview
                visible={visibleReviewBilling}
                onCancel={offModalReviewBilling}
              />
            )}
          </StyledModal>
        );
      }}
    </CODChargeValidating>
  );
};

export const RequiredActiveProductSection = ({ entityId, entityType }) => {
  const { requiredForm, currentStep } = useRequiredActiveProductContext();

  const { flatRequiredProductFields } = useAllMinimumRequirementProductFields();

  const additionalFields = Object.keys(additionalRequirementFields);

  const {
    entityRequirement,
    isEmptyEntityRequirement,
    isLoading: isLoadingRequirementDetail,
  } = useGetMinimumRequirementDetail({
    entityId,
    entityName: entityType,
  });

  const {
    requiredErrorFields,
    isMatched,
    isLoading: isLoadingErrorFields,
  } = useValidateMinimumRequirement({
    requirementId: entityRequirement?.id,
    productId: entityId,
  });

  const filteredEntityRequirement = useMemo(() => {
    let result = [];

    if (
      entityRequirement?.requiredProperties?.length > 0 &&
      Object.keys(flatRequiredProductFields)?.length > 0
    ) {
      entityRequirement.requiredProperties.forEach((requiredItem) => {
        const foundProperty = flatRequiredProductFields[`${requiredItem}`];
        foundProperty && result.push(foundProperty);
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRequirement, Object.keys(flatRequiredProductFields)?.length]);

  const groupedRequiredProperties = useMemo(() => {
    if (filteredEntityRequirement.length === 0) return [];

    return groupPropertiesByModuleName(filteredEntityRequirement);
  }, [filteredEntityRequirement]);

  const additionalRequiredErrorFields = requiredErrorFields.filter((field) =>
    additionalFields.includes(field)
  );

  let element = null;

  if (isEmptyEntityRequirement) {
    element = <ActiveProductRequirement productIds={[entityId]} />;
  } else {
    element =
      currentStep === 0 ? (
        <Row
          style={{ width: '100%' }}
          gutter={[0, 6]}
          className='required-section'
        >
          {groupedRequiredProperties?.length > 0 && (
            <RequiredPropertiesSection header='Required Properties'>
              <RequiredPropertiesForm
                form={requiredForm}
                style={{ width: '100%' }}
              >
                {groupedRequiredProperties.map((groupItem) => {
                  return (
                    <Col span={24}>
                      <RequiredModuleName
                        moduleName={groupItem.moduleDisplayName}
                        key={groupItem.moduleDisplayName}
                      />
                      {groupItem.moduleProperties.map((property) => {
                        const isError = requiredErrorFields.includes(
                          property.fieldFullPath
                        );

                        return (
                          <RequiredPropertyItem
                            key={property.fullPathDisplayName}
                          >
                            <RequiredPropertyName
                              key={property.fullPathDisplayName}
                              propertyName={property.fullPathDisplayName}
                              isError={isError}
                            />
                            <RequiredPropertyStatus
                              status={
                                isLoadingErrorFields
                                  ? 'loading'
                                  : isError
                                  ? 'error'
                                  : 'success'
                              }
                            />

                            {isError && (
                              <RequiredPropertyInput
                                property={property}
                                status='error'
                              />
                            )}
                          </RequiredPropertyItem>
                        );
                      })}
                    </Col>
                  );
                })}
              </RequiredPropertiesForm>
            </RequiredPropertiesSection>
          )}

          {additionalRequiredErrorFields?.length > 0 && (
            <RequiredPropertiesSection header='Additional Properties'>
              <AdditionalRequiredActiveProduct
                requiredErrorFields={additionalRequiredErrorFields}
                entityId={entityId}
              />
            </RequiredPropertiesSection>
          )}
        </Row>
      ) : (
        <ActiveProductRequirement productIds={[entityId]} />
      );
  }

  return (
    <>
      <Steps
        current={isEmptyEntityRequirement ? STEP_CHARGE : currentStep}
        style={{
          width: '80%',
          margin: 'auto',
          marginTop: 12,
        }}
      >
        <Step
          key={0}
          title={
            <Typography.Text strong style={{ fontSize: 16 }}>
              {isEmptyEntityRequirement
                ? 'Passed Requirements'
                : 'Update Requirements'}
            </Typography.Text>
          }
          icon={
            <>
              {isEmptyEntityRequirement ? (
                <CheckCircleFilled
                  style={{
                    color: '#52C41A',
                  }}
                />
              ) : isLoadingErrorFields ? (
                <LoadingOutlined />
              ) : isMatched ? (
                <CheckCircleFilled
                  style={{
                    color: '#52C41A',
                  }}
                />
              ) : (
                <CloseCircleFilled
                  style={{
                    color: '#f5222d',
                  }}
                />
              )}
            </>
          }
        />
        <Step
          key={1}
          icon={<CreditCardFilled />}
          title={
            <Typography.Text strong style={{ fontSize: 16 }}>
              Charge
            </Typography.Text>
          }
        />
      </Steps>
      <div style={{ paddingTop: 24 }}>
        <WithLoading loading={isLoadingRequirementDetail}>
          {element}
        </WithLoading>
      </div>
    </>
  );
};

const ActiveProductRequirement = ({ productIds, children }) => {
  const { passedProducts } = useValidateActiveProduct({
    productIds,
  });

  const { columns } = useIXOneColumns({ isActiveProduct: true });

  const totalFee = useCalculateFeeActiveProduct({ productIds });

  return (
    <>
      <IXOneHeader style={{ minHeight: 110 }} />
      <IXOneMessage />
      <IXOneGrid
        columns={columns}
        productList={passedProducts}
        style={{ height: 400 }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            marginTop: 4,
          }}
        >
          {children}
          <ActiveProductRequirementFooter />
          <IXOneTotalFee fee={totalFee} />
        </div>
      </IXOneGrid>
    </>
  );
};

const ActiveProductRequirementFooter = () => {
  const [visibleReviewBilling, { onModal, offModal }] = useVisibleModal();

  return (
    <div>
      <Button
        type='link'
        style={{ paddingLeft: 0, textDecoration: 'underline' }}
        onClick={onModal}
      >
        <Typography.Text
          style={{ textDecoration: 'underline', color: '#238dd9' }}
        >
          Review the RIVIR Billing T&Cs
        </Typography.Text>
      </Button>
      {visibleReviewBilling && (
        <RIVIRBillingReview
          visible={visibleReviewBilling}
          onCancel={offModal}
        />
      )}
    </div>
  );
};
